import { useEffect, useState } from "react";
import { ListCapturesQuery, StoredCapture } from "../../../types";
import { ApiError, CaptureApi } from "../api";
import { useEnv } from "../providers/envProvider";
import { href, navigate } from "../route";
import { CaptureFilters, Filters, filtersFromParams } from "./Filters";
import { CaptureRow, matchesQuery } from "./CaptureRow";
import { Button, Panel } from "./Shell";

const toQuery = (env: string, f: CaptureFilters, cursor?: string): ListCapturesQuery => ({
  env,
  level: f.level ? (f.level.split(",") as ListCapturesQuery["level"]) : undefined,
  source: f.source,
  lambdaName: f.lambdaName,
  userId: f.userId,
  flowId: f.flowId,
  status: f.status as ListCapturesQuery["status"],
  from: f.from,
  to: f.to,
  order: f.order === "asc" ? "asc" : "desc",
  limit: 50,
  cursor,
});

export const CaptureList = ({ params }: { params: URLSearchParams }) => {
  const filters = filtersFromParams(params);
  const { env } = useEnv();
  const [items, setItems] = useState<StoredCapture[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const key = `${env}|${params.toString()}`;

  const load = async (reset: boolean) => {
    setLoading(true);
    setError(undefined);
    try {
      const page = await CaptureApi.list(toQuery(env, filters, reset ? undefined : cursor));
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
      setCursor(page.cursor);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const setFilters = (next: CaptureFilters) => navigate(href.captures(next as Record<string, string | undefined>));
  const visible = items.filter((c) => matchesQuery(c, filters.q));

  return (
    <div className="space-y-3">
      <Panel className="p-3">
        <Filters value={filters} onChange={setFilters} />
      </Panel>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Panel>
        {visible.map((c) => (
          <CaptureRow key={c.id} capture={c} showGroupLink />
        ))}
        {!loading && visible.length === 0 && <p className="p-4 text-sm text-neutral-500">No captures match.</p>}
        <div className="p-2 flex items-center gap-2 text-xs text-neutral-500">
          <span>{visible.length} shown{filters.q && items.length !== visible.length ? ` of ${items.length} loaded` : ""}</span>
          <span className="flex-1" />
          {cursor && <Button onClick={() => load(false)} disabled={loading}>{loading ? "Loading…" : "Load more"}</Button>}
        </div>
      </Panel>
    </div>
  );
};
